import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

export default function Protected({ children, allow, module, addon, features }) {
  const { user, tenant, hiddenModules, enabledAddons, effectiveFeatures } = useAuth();
  const loc = useLocation();
  if (user === null) return <div className="p-6">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;

  // 1. Forced password change comes first — and BLOCKS all other guards
  // until satisfied. Otherwise the onboarding guard below would yank
  // the owner back to /onboarding, which then re-yanks to /change-password.
  if (user.must_reset_password) {
    return loc.pathname === "/change-password"
      ? children
      : <Navigate to="/change-password" replace />;
  }

  // 2. Owner whose tenant hasn't been onboarded yet → onboarding wizard.
  // We treat anything other than an explicit `true` as "not yet done"
  // (covers brand-new tenants where the field isn't set on the doc).
  //
  // Exception: when the owner clicks "Save & finish later" in the wizard,
  // we set sessionStorage.vb_onboarding_paused so they can browse the
  // rest of the app for this session. The flag is automatically cleared
  // on the next sign-in or page reload, so the wizard reappears.
  const finishLaterFlag =
    typeof window !== "undefined" && window.sessionStorage?.getItem("vb_onboarding_paused") === "1";
  if (
    user.role === "owner" &&
    tenant &&
    tenant.onboarding_completed !== true &&
    !finishLaterFlag &&
    loc.pathname !== "/onboarding"
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  // 3. Role-allowlist enforcement
  if (allow && !allow.includes(user.role)) return <Navigate to="/app" replace />;
  // 4. Globally-hidden module (phased rollout) — super_admin still passes.
  if (module && user.role !== "super_admin" && (hiddenModules || []).includes(module)) {
    return <Navigate to="/app" replace />;
  }
  // 5. Per-tenant add-on entitlement — super_admin still passes.
  if (addon && user.role !== "super_admin" && !(enabledAddons || []).includes(addon)) {
    return <Navigate to="/app" replace />;
  }
  // 6. Per-tenant feature flag(s) — route is visible if ANY of the listed
  // features is enabled for the tenant. super_admin always passes.
  if (features && features.length && user.role !== "super_admin") {
    const feat = effectiveFeatures || {};
    const anyOn = features.some((f) => feat[f] !== false);
    if (!anyOn) return <Navigate to="/app" replace />;
  }
  return children;
}
